/**
 * @fileoverview Base class for modal dialogs with common Bootstrap modal functionality.
 * @module ui/BaseDialog
 */

import { generateUniqueId, escapeHtml } from '../utils/jrfuncs.js';


/**
 * Registry tracking which dialog classes currently have an open instance.
 * Used by showSingleton() to prevent multiple dialogs of the same type.
 * @type {Set<Function>}
 */
const openDialogs = new Set();


/**
 * Base class for modal dialogs providing common Bootstrap modal patterns.
 *
 * Subclasses must implement:
 * - getModalHtml() - Returns the modal HTML structure
 *
 * Subclasses may override:
 * - setupEventListeners() - Sets up dialog-specific event handlers
 * - onBeforeShow() - Called before the modal is shown
 * - getDefaultResult() - Returns the default result when cancelled
 *
 * @class
 * @abstract
 */
export class BaseDialog {

    /**
     * Creates a new BaseDialog instance.
     * @param {Object} [options={}] - Configuration options
     * @param {string} [options.idPrefix='dialog'] - Prefix for the modal ID
     */
    constructor(options = {}) {
        this.options = options;
        this.modalId = generateUniqueId(`${options.idPrefix || 'dialog'}_`);
        this.modalElement = null;
        this.modal = null;
        this.resolvePromise = null;
    }


    /**
     * Shows the dialog and returns a Promise that resolves when closed.
     * @returns {Promise<*>} Promise resolving to dialog result
     */
    show() {
        return new Promise((resolve) => {
            this.resolvePromise = resolve;
            this.createModal();
            this.setupEventListeners();
            this.modal.show();
        });
    }


    /**
     * Creates the modal DOM structure and initializes Bootstrap modal.
     * @private
     */
    createModal() {
        const modalHtml = this.getModalHtml();
        document.body.insertAdjacentHTML('beforeend', modalHtml);
        this.modalElement = document.getElementById(this.modalId);
        this.modal = new bootstrap.Modal(this.modalElement);

        // Handle modal hidden event (cancel via X, backdrop, or Escape)
        this.modalElement.addEventListener('hidden.bs.modal', () => {
            if (this.resolvePromise) {
                this.resolve(this.getDefaultResult());
            }
        });

        // Blur focused element before hiding to avoid aria-hidden warning
        this.modalElement.addEventListener('hide.bs.modal', () => {
            if (document.activeElement && this.modalElement?.contains(document.activeElement)) {
                document.activeElement.blur();
            }
        });

        this.onBeforeShow();

        // Fire onAfterShow once the modal transition completes
        this.modalElement.addEventListener('shown.bs.modal', () => {
            this.onAfterShow();
        }, { once: true });
    }


    /**
     * Returns the HTML for the modal. Must be implemented by subclasses.
     * @returns {string} Modal HTML string
     * @abstract
     */
    getModalHtml() {
        throw new Error('Subclass must implement getModalHtml()');
    }


    /**
     * Sets up dialog-specific event listeners.
     * Override in subclasses for custom behavior.
     */
    setupEventListeners() {
        // Override in subclasses
    }


    /**
     * Called before the modal is shown.
     * Override in subclasses for initialization.
     */
    onBeforeShow() {
        // Override in subclasses
    }

    /**
     * Called after the modal is fully shown (transition complete).
     * Override in subclasses for post-display initialization.
     */
    onAfterShow() {
        // Override in subclasses
    }


    /**
     * Returns the default result when the dialog is cancelled.
     * Override in subclasses if a different default is needed.
     * @returns {*} Default result (null by default)
     */
    getDefaultResult() {
        return null;
    }


    /**
     * Closes the dialog and resolves with the given result.
     * Safe to call multiple times - subsequent calls are ignored.
     * @param {*} result - The result to resolve with
     */
    resolve(result) {
        if (!this.resolvePromise) {
            return;
        }

        const resolveFunc = this.resolvePromise;
        const modal = this.modal;
        const modalElement = this.modalElement;

        // Clear references to prevent re-entry
        this.resolvePromise = null;
        this.modal = null;
        this.modalElement = null;

        const cleanup = () => {
            if (modal) {
                try { modal.dispose(); } catch (e) { /* ignore */ }
            }
            if (modalElement?.parentNode) {
                modalElement.remove();
            }
            resolveFunc(result);
        };

        if (modal && modalElement?.classList.contains('show')) {
            // Modal is still visible — hide it and wait for the animation
            // to finish before resolving, so callers don't modify the DOM
            // while Bootstrap's transition is still running

            // Blur focused element before hiding to avoid aria-hidden warning
            // (must use local modalElement since this.modalElement is already null)
            if (document.activeElement && modalElement.contains(document.activeElement)) {
                document.activeElement.blur();
            }

            modalElement.addEventListener('hidden.bs.modal', cleanup, { once: true });
            modal.hide();
        } else {
            // Modal already hidden (dismissed via backdrop/Escape/X) — resolve immediately
            cleanup();
        }
    }


    // ==========================================
    // HELPER METHODS
    // ==========================================

    /**
     * Helper to query elements by ID suffix within this modal.
     * @param {string} suffix - ID suffix (e.g., '_textarea', '_saveBtn')
     * @returns {Element|null} The found element or null
     */
    getElement(suffix) {
        return this.modalElement?.querySelector(`#${this.modalId}${suffix}`);
    }


    /**
     * Helper to query elements within this modal.
     * @param {string} selector - CSS selector
     * @returns {Element|null} The found element or null
     */
    querySelector(selector) {
        return this.modalElement?.querySelector(selector);
    }


    // ==========================================
    // MODAL HTML TEMPLATE BUILDER
    // ==========================================

    /**
     * Builds standardized modal HTML with common structure.
     * @param {Object} config - Modal configuration
     * @param {string} config.title - Modal title text
     * @param {string} [config.icon] - Bootstrap icon class
     * @param {string} config.bodyHtml - HTML content for modal body
     * @param {string} config.footerHtml - HTML content for modal footer
     * @param {string} [config.size='modal-lg'] - Modal size class
     * @param {string} [config.infoText] - Optional info text at top of body
     * @returns {string} Complete modal HTML string
     */
    buildModalHtml(config) {
        const { title, icon, bodyHtml, footerHtml, size = 'modal-lg', infoText } = config;
        // the title is PLAIN TEXT, escaped here: callers build it from data -- an imported
        // bookmark's label, a PDF outline title -- and every caller passes text, never markup
        const titleHtml = escapeHtml(title);
        const iconHtml = icon ? `<i class="bi ${icon}"></i> ` : '';
        const infoHtml = infoText ? `<div class="dialogInfoText">${infoText}</div>` : '';

        return `
            <div class="modal fade" id="${this.modalId}" tabindex="-1" aria-labelledby="${this.modalId}Label" aria-hidden="true">
                <div class="modal-dialog modal-dialog-centered ${size}">
                    <div class="modal-content">
                        <div class="modal-header">
                            <h5 class="modal-title" id="${this.modalId}Label">${iconHtml}${titleHtml}</h5>
                            <button type="button" class="btn-close" data-bs-dismiss="modal" aria-label="Close"></button>
                        </div>
                        <div class="modal-body">
                            ${infoHtml}
                            ${bodyHtml}
                        </div>
                        <div class="modal-footer">
                            ${footerHtml}
                        </div>
                    </div>
                </div>
            </div>
        `;
    }


    // ==========================================
    // STATIC SINGLETON HELPER
    // ==========================================

    /**
     * Shows a dialog as a singleton - only one instance of each dialog class
     * can be open at a time. Subsequent calls while open return the default result.
     *
     * Usage in subclass:
     *   static async showMyDialog(options) {
     *       return BaseDialog.showSingleton(MyDialog, options);
     *   }
     *
     * @param {Function} DialogClass - The dialog class to instantiate
     * @param {Object} [options={}] - Options passed to the dialog constructor
     * @returns {Promise<*>} Promise resolving to dialog result
     */
    static async showSingleton(DialogClass, options = {}) {
        if (openDialogs.has(DialogClass)) {
            // Return default result for this dialog type
            const tempInstance = new DialogClass(options);
            return tempInstance.getDefaultResult();
        }

        openDialogs.add(DialogClass);
        try {
            const dialog = new DialogClass(options);
            return await dialog.show();
        } finally {
            openDialogs.delete(DialogClass);
        }
    }
}
